/**
 * Defensive `BigInt(...)` wrapper for the lobby's hot render paths.
 *
 * Indexer / SSE / RPC payloads occasionally land with shapes that a bare
 * `BigInt(value)` call rejects synchronously — `null`, `undefined`,
 * empty strings, decimal strings like `'0.0'`, scientific notation
 * (`'1e18'`), or already-bigint values that re-enter the parser. A
 * synchronous throw inside a React render path propagates to the
 * route-group `error.tsx` and looks to the user like a 500. The seats
 * map in `buildSeatData` runs every time `mergedRawSeats` updates, so
 * a single bad row from a partial SSE patch was enough to take the
 * whole lobby down for a specific wallet on cellular (cold-cache
 * + slow network widens the race window the existing `error.tsx`
 * docstring already calls out).
 *
 * The contract here is "be permissive in what you accept, fall back to
 * a safe zero when in doubt":
 *
 *   - `bigint` round-trips unchanged.
 *   - `number` truncates to integer first (`Math.trunc`) so `1.5` →
 *     `1n` rather than throwing on `BigInt(1.5)`.
 *   - `string` strips a trailing decimal tail (`'0.0'` → `'0'`,
 *     `'1.5'` → `'1'`) — most of our payloads are wei-precision
 *     integers but a poorly-cast indexer field occasionally returns
 *     `'0.0'`. We *don't* try to parse scientific notation: the
 *     `BigInt('1e18')` throw is caught and we return the fallback.
 *   - Anything else (null/undefined/object/'NaN'/'undefined'/etc.)
 *     returns the fallback.
 *
 * The fallback defaults to `0n` because every consumer in the lobby
 * treats a missing balance / price / yield as "no value" anyway —
 * surfacing zero is strictly better than crashing the tree.
 */
export function safeBigInt(value: unknown, fallback: bigint = 0n): bigint {
  if (typeof value === 'bigint') return value;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return fallback;
    try {
      return BigInt(Math.trunc(value));
    } catch {
      return fallback;
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return fallback;
    // Strip a single decimal tail so `'0.0'` / `'1.5'` parse to their
    // integer part instead of throwing. Anything else (scientific
    // notation, hex prefixes BigInt already supports, etc.) flows
    // through to the raw `BigInt` call below.
    const head = trimmed.includes('.') ? trimmed.slice(0, trimmed.indexOf('.')) : trimmed;
    // After trimming a leading-decimal value (e.g. `'.5'`), `head` can
    // be empty or just a sign — treat those as the fallback rather
    // than calling `BigInt('')` / `BigInt('-')`.
    if (head === '' || head === '-' || head === '+') return fallback;
    try {
      return BigInt(head);
    } catch {
      return fallback;
    }
  }

  return fallback;
}
